/**
 * A class which represents a DatePicker widget.
 * This defines the structure and the main behavior of the widget.
 *
 * @param $markup The jQuery element which is the DatePicker markup.
 */

import 'qtip2/dist/jquery.qtip.css';
import '../../Tooltip/styles/tooltip.less';
import '../styles/datePicker.less';
import 'qtip2';
import $ from 'jquery';
import * as common from '../../../shared/modules/common';
import i18n from '../../../shared/modules/i18n';
import { init as initInput } from '../../Input/js/input';
import Calendar from './datePickerCalendar';
import Icon from './datePickerIcon';
import tooltip from './datePickerTooltip';
import View from './datePickerView';

function DatePicker($markup) {
    var markup = $markup[0],
        calendar,
        icon,
        model;

    /**
     * Creates model for DatePicker
     *
     * @return A JSON object, which contains the following parameters:
     *      getDateFormat: a function which returns the date format string of value,
     *      i18n: the i18n JSON object with keys and resolved values,
     *      iconPlacement: the placement of the date picker icon button depends on the opening mode
     *          (click mode: outside, focus mode: inside),
     *      openingMode: the opening mode of Date Picker (focus or click),
     *      placement: priority list of the calendar page overlay
     *      value: the value string of selected date,
     *      weekStart: the integer value of week starting day (0..6: Sunday to Saturday)
     */
    function buildModel() {
        /**
         * @return the date format string of value,
         *      default: 'DD.MM.YYYY'
         */
        function getDateFormat() {
            return common.data($markup, 'date-format') || 'DD.MM.YYYY';
        }

        /**
         * Returns an object with the resolved i18n key-value pairs. See the i18n module for details on how
         * the resolution is done.
         *
         * @return the i18n hash object, with the following keys:
         *      done: label of done button
         *      legendText: description for non-selectable days
         *      months: array of month names starting from January
         *      monthsShort: array of short month names starting from January
         *      selectMonth: title of month selector view
         *      today: label of today button
         *      todayAbbrev: label of Today within the calendar (day selector) view
         *      weekdaysShort: array of short weekday names starting from Monday
         */
        function getI18n() {
            var keys = ['done', 'legendText', 'months', 'monthsShort',
                        'selectMonth', 'today', 'todayAbbrev', 'weekdaysShort'],
                keyPrefix = 'datepicker',
                messages = i18n({
                    $markup: $markup,
                    keyPrefix: keyPrefix
                }),
                resolvedKeys = {};

            $.each(keys, function (i, key) {
                resolvedKeys[key] = messages.get(key);
            });

            resolvedKeys.months = resolvedKeys.months.split(',');
            resolvedKeys.monthsShort = resolvedKeys.monthsShort.split(',');
            resolvedKeys.weekdaysShort = resolvedKeys.weekdaysShort.split(',');

            return resolvedKeys;
        }

        /**
         * @return the placement of the date picker icon button depends on the opening mode
         *      (click mode: outside, focus mode: inside),
         *      default: 'inside'
         */
        function getIconPlacement() {
            return getOpeningMode() === 'click' ? 'outside' : 'inside';
        }

        /**
         * @return the opening mode of Date Picker (focus or click),
         *      default: 'focus'
         */
        function getOpeningMode() {
            return common.data($markup, 'opening-mode') === 'click' ? 'click' : 'focus';
        }

        /**
         * @return the placement priority list of the calendar page overlay,
         *      default: ['right']
         */
        function getPlacement() {
            var data = common.data($markup, 'placement');
            return data ? data.split(' ') : ['right'];
        }

        /**
         * @return the integer value of week starting day (0..6: Sunday  to Saturday),
         *      default: 1
         */
        function getWeekStart() {
            var weekStart = common.data($markup, 'week-start');
            return weekStart >= 0 && weekStart <= 6 ? weekStart : 1;
        }

        return {
            'getDateFormat': getDateFormat,
            'i18n': getI18n(),
            'iconPlacement': getIconPlacement(),
            'openingMode': getOpeningMode(),
            'placement': getPlacement(),
            'weekStart': getWeekStart()
        };
    }

    /**
     * Initialize widget view
     */
    function init() {
        var button,
            buttonSelector,
            $target;

        model = buildModel();

        $markup
            .wrap(View.inputContainer)
            .addClass('uwr-input')
            .attr('maxlength', model.getDateFormat().length);

        icon = new Icon({
            'iconPlacement': model.iconPlacement,
            '$input': $markup
        });

        $target = model.openingMode === 'click' ? icon.getView() : $markup;

        calendar = new Calendar({
            'getDateFormat': model.getDateFormat,
            'i18n': model.i18n,
            'weekStart': model.weekStart,
            '$input': $markup,
            '$target': $target
        });

        initInput($markup);

        if (!$markup.is(':disabled')) {
            tooltip({
                'calendar': calendar,
                'icon': icon,
                'openingMode': model.openingMode,
                'placement': model.placement,
                '$target': $target
            });
        } else {
            buttonSelector = common.data($markup, 'icon-button-selector');
            button = buttonSelector ? document.querySelector(buttonSelector)
                : icon.getView()[0].querySelector('.uwr-button'); // icon is outside
            button.setAttribute('disabled', '');
        }

    }

    if (common.shouldBeInitialized(markup)) {
        init();
        // removed 'markInitialized' because input already did it at line 173
        // common.markInitialized(markup);
    }
}

/**
 * Initialize the selected Date Picker(s)
 * Supported use cases:
 *   - init();
 *   - init(callback);
 *   - init(selector);
 *   - init(selector, callback);
 * @param selector CSS selector string that returns the elements that need to be initialized.
 *   Optional: If not passed in, then all elements marked with "uwr-date-picker" CSS class will be processed.
 * @param callback The function to run after initialization (optional).
 */
export function init(selector, callback) {
    var callbackWithoutSelector = arguments.length === 1 && typeof selector === 'function';
    if (arguments.length === 0 || callbackWithoutSelector) {
        $('.uwr-date-picker').each(function () {
            /* eslint-disable no-new */
            new DatePicker($(this));
            /* eslint-enable no-new */
        });
        if (callbackWithoutSelector) {
            common.invoke(selector);
        }
    } else {
        /* eslint-disable no-new */
        new DatePicker($(selector));
        /* eslint-enable no-new */
        common.invoke(callback);
    }
}



//////////////////
// WEBPACK FOOTER
// ./widgets/DatePicker/js/datePicker.js
// module id = 29
// module chunks = main